import { Button } from "$components/button/button";
import { Dialog } from "$components/dialog/dialog";
import { styled, Typography } from "@material-ui/core";
import { useIntl } from "react-intl";

const StyledDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    width: "80%",
    maxWidth: "512px",
  },
});

const MarginTopTypography = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

const Content = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  padding: "20px",
  backgroundColor: theme.palette.grey[100],
  borderRadius: "8px",
}));

const ContentItem = styled("div")({
  display: "flex",
  flexDirection: "column",
});

const ContentItemTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
}));

const ContentItemValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 700,
}));

const Footer = styled("div")({
  display: "flex",
  justifyContent: "space-between",
});

export const LinkAccountModal: React.FC<{
  patient?: {
    cardId?: string;
    fullName: string;
    phone?: string;
    address?: string;
  };
  open: boolean;
  onClose: () => void;
  onLink: () => void;
}> = ({ patient, open, onClose, onLink }) => {
  const { formatMessage } = useIntl();

  if (!patient) {
    return null;
  }

  const details = [
    {
      titleId: "linkModal.cardId",
      defaultMessage: "Elephant ID",
      value: patient.cardId || "-",
    },
    {
      titleId: "linkModal.fullName",
      defaultMessage: "Full name",
      value: patient.fullName || "-",
    },
    {
      titleId: "linkModal.phone",
      defaultMessage: "Phone",
      value: patient.phone || "-",
    },
    {
      titleId: "linkModal.address",
      defaultMessage: "Address",
      value: patient.address || "-",
    },
  ];

  return (
    <StyledDialog
      open={open}
      onCancel={onClose}
      title={formatMessage({
        id: "linkModal.title",
        defaultMessage: "Link to the guardian's account",
      })}
    >
      <Typography variant="body1">
        {formatMessage({
          id: "linkModal.description1",
          defaultMessage:
            "We found a patient with matching details. You can link these accounts for easier access to the child’s record and extra reminders.",
        })}
      </Typography>
      <MarginTopTypography variant="body1">
        {formatMessage({
          id: "linkModal.description2",
          defaultMessage: "Do you want to link these accounts?",
        })}
      </MarginTopTypography>
      <Content>
        {details.map((detail) => (
          <ContentItem key={detail.titleId}>
            <ContentItemTitle variant="body1">
              {formatMessage({
                id: detail.titleId,
                defaultMessage: detail.defaultMessage,
              })}
            </ContentItemTitle>
            <ContentItemValue variant="body1">{detail.value}</ContentItemValue>
          </ContentItem>
        ))}
      </Content>
      <Footer>
        <Button variant="text" color="primary" onClick={onClose}>
          {formatMessage({
            id: "linkModal.cancel",
            defaultMessage: "Not now",
          })}
        </Button>
        <Button variant="contained" color="primary" onClick={onLink}>
          {formatMessage({
            id: "linkModal.link",
            defaultMessage: "Link accounts",
          })}
        </Button>
      </Footer>
    </StyledDialog>
  );
};
